// eslint-disable-next-line @typescript-eslint/no-restricted-imports
import { IntlMessageFormat, type Formats, type FormatXMLElementFn, type Options } from 'intl-messageformat';
type Locale = 'de' | 'en' | 'es' | 'fr' | 'it';
export type RichTextFormatter = FormatXMLElementFn<JSX.Element>;
type MessageFormatterFormatOutput<T> = T extends RichTextFormatter ? JSX.Element : string;
export type TranslationDefinition<TranslationKey extends string = string> = {
  key?: TranslationKey;
  id: TranslationKey;
  description: string;
  defaultMessage: string;
  locales?: Locale[];
};
export function translate<TranslationKey extends string>(definitions // replaced by i18nLoader
: Record<TranslationKey, TranslationDefinition> | TranslationDefinition<TranslationKey>[]): Record<TranslationKey, string> {
  return definitions as Record<TranslationKey, string>;
}
export type MessageFormatterReturnType<T = string> = string | T | (string | T)[];
export type MessageFormatter<Props extends Record<string, any>> = {
  format<Output = MessageFormatterFormatOutput<Props[keyof Props]>>(props: Props): MessageFormatterReturnType<Output>;
  formatter: IntlMessageFormat;
};
export function translateFormatter<Props extends Record<string, any>>(definition: TranslationDefinition, overrideFormats?: Partial<Formats>, options?: Options, locale?: Locale): MessageFormatter<Props> {
  const formatter = new IntlMessageFormat(definition as unknown as string,
  // replaced by i18nLoader
  locale,
  // replaced by i18nLoader
  overrideFormats, {
    ...options,
    requiresOtherClause: false
  });
  return {
    format: <Output,>(props: Props) => {
      return formatter.format(props) as Output;
    },
    formatter
  };
}
type InnerTranslateFormatterWithJSONShape = {
  [key: string]: Record<string, unknown> | 'string';
};
type TranslateFormatterWithJSONShape = InnerTranslateFormatterWithJSONShape[] | InnerTranslateFormatterWithJSONShape;
type OptionalKeys<T> = {
  // eslint-disable-next-line @typescript-eslint/ban-types
[K in keyof T]-?: {} extends Pick<T, K> ? K : never }[keyof T];
type JSONValueFormatter<Props extends TranslateFormatterWithJSONShape> = Props extends Array<infer U extends TranslateFormatterWithJSONShape> ? JSONValueFormatter<U>[] : Props extends InnerTranslateFormatterWithJSONShape ? { [key in OptionalKeys<Props>]?: NonNullable<Props[key]> extends Record<string, unknown> ? MessageFormatter<NonNullable<Props[key]>> : MessageFormatter<Record<string, never>> } & { [key in Exclude<keyof Props, OptionalKeys<Props>>]: Props[key] extends Record<string, unknown> ? MessageFormatter<Props[key]> : MessageFormatter<Record<string, never>> } : never;
export function translateWithJSONValue<Props extends TranslateFormatterWithJSONShape>(definition: TranslationDefinition, overrideFormats?: Partial<Formats>, options?: Options, locale?: Locale): JSONValueFormatter<Props> {
  const value = definition as unknown as Props; // replaced by i18nLoader

  const formatterForObject = (arg: InnerTranslateFormatterWithJSONShape) => {
    return Object.fromEntries(Object.entries(arg).map(([key, value]) => {
      const formatter = new IntlMessageFormat(value as unknown as string,
      // replaced by i18nLoader
      locale,
      // replaced by i18nLoader
      overrideFormats, {
        ...options,
        requiresOtherClause: false
      });
      return [key, {
        format: <Output,>(props: Record<string, unknown>) => {
          return formatter.format(props) as Output;
        },
        formatter
      }];
    }));
  };
  if (Array.isArray(value)) {
    return value.map(formatterForObject) as unknown as JSONValueFormatter<Props>;
  }
  return formatterForObject(value) as unknown as JSONValueFormatter<Props>;
}